import { trackById } from 'src/app/shared/utils/track-by.util';
import { Component, OnInit, OnDestroy, OnChanges, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { takeUntil, finalize } from "rxjs/operators";
import { IDashboardWidget } from "src/app/models/dashboard";
import { SampleSurveyService } from "src/app/services/sample-survey.service";
import { CommonService } from "src/app/services/common.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { SurveyProgressItem } from "src/app/models/survey-progress.model";
import { SURVEY_GRID_ROUTE } from "src/app/constants/dashboard.constants";
import { navigateToGridWithOrgFilter } from "src/app/constants/dashboard-navigation.utils";
import { WidgetOrgFilterComponent } from "src/app/common-components/widget-org-filter/widget-org-filter.component";
import {
    SurveyStatus,
    SurveyStatusLabels,
    SurveyStatusCssClasses,
} from "src/app/enums/surveyStatus.enum";
import { BaseDashboardWidget } from "src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget";

interface SurveyProgressVm extends SurveyProgressItem {
    statusLabel: string;
    statusClass: string;
    formattedStartDate: string;
    formattedEndDate: string;
}

@Component({
    standalone: true,
    selector: "app-survey-progress-widget",
    imports: [CommonModule, TranslateModule, WidgetOrgFilterComponent],
    templateUrl: "./survey-progress-widget.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./survey-progress-widget.component.scss"]
})
export class SurveyProgressWidgetComponent
    extends BaseDashboardWidget
    implements OnInit, OnDestroy, OnChanges, IDashboardWidget
{
    surveys: SurveyProgressVm[] = [];
    surveyStatusLabels = new Map<number, string>(
        Object.entries(SurveyStatusLabels).map(([k, v]) => [+k, v])
    );

    constructor(
        private surveyService: SampleSurveyService,
        private commonService: CommonService,
        private router: Router,
        private dateFormatService: DateFormatService,
    ) {
        super();
    }

    protected override reload(): void {
        this.loadSurveyProgress();
    }

    private loadSurveyProgress(): void {
        this.isLoading = true;
        this.error = undefined;

        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();
        this.surveyService
            .getSurveyProgress(effectiveOrgId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe({
                next: (response) => {
                    this.surveys = (response.surveys || []).map(s => ({
                        ...s,
                        statusLabel: SurveyStatusLabels[s.surveyStatus] ?? 'Unknown',
                        statusClass: SurveyStatusCssClasses[s.surveyStatus] ?? '',
                        formattedStartDate: this.formatDate(s.startDate),
                        formattedEndDate: this.formatDate(s.endDate),
                    }));
                },
                error: () => {
                    this.error = "dashboard.widgets.surveyProgress.loadError";
                    this.surveys = [];
                },
            });
    }

    navigateToSurvey(id: string): void {
        this.router.navigate([`/dashboard/survey-details/${id}`]);
    }

    navigateToSurveys(): void {
        const orgOverrideId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        navigateToGridWithOrgFilter(this.router, this.commonService, SURVEY_GRID_ROUTE, {}, orgOverrideId);
    }

    getSurveyStatusLabel(status: SurveyStatus): string {
        return SurveyStatusLabels[status] ?? "Unknown";
    }

    getSurveyStatusClass(status: SurveyStatus): string {
        return SurveyStatusCssClasses[status] ?? "";
    }

    formatDate(dateStr: string | null): string {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "-";
        return this.dateFormatService.formatDateTimeToString(
            date,
            this.dateFormatService.userDateFormat,
        );
    }

    public trackById = trackById;
}
